import type { ApiResponse, HttpMethod } from './types';

function getBaseUrl(): string {
  return (localStorage.getItem('ss_api_url') || '').replace(/\/+$/, '');
}

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('ss_refresh_token');
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${getBaseUrl()}/api/users/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) return null;

    const data = await res.json();
    localStorage.setItem('ss_jwt_token', data.token);
    localStorage.setItem('ss_refresh_token', data.refreshToken);
    return data.token;
  } catch {
    return null;
  }
};

export const api = async <T = unknown>(
  method: HttpMethod,
  urlPath: string,
  body?: unknown,
  token?: string,
): Promise<ApiResponse<T>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['x-auth-token'] = token;

  const res = await fetch(`${getBaseUrl()}${urlPath}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['x-auth-token'] = newToken;
      const retryRes = await fetch(`${getBaseUrl()}${urlPath}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      let data: T;
      try {
        data = await retryRes.json();
      } catch {
        data = (await retryRes.text()) as unknown as T;
      }
      return { status: retryRes.status, data };
    }
  }

  let data: T;
  try {
    data = await res.json();
  } catch {
    data = (await res.text()) as unknown as T;
  }
  return { status: res.status, data };
};

export const uploadFile = async <T = unknown>(
  urlPath: string,
  formData: FormData,
  token?: string,
): Promise<ApiResponse<T>> => {
  const headers: Record<string, string> = {};
  if (token) headers['x-auth-token'] = token;

  const res = await fetch(`${getBaseUrl()}${urlPath}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['x-auth-token'] = newToken;
      const retryRes = await fetch(`${getBaseUrl()}${urlPath}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      let data: T;
      try {
        data = await retryRes.json();
      } catch {
        data = (await retryRes.text()) as unknown as T;
      }
      return { status: retryRes.status, data };
    }
  }

  let data: T;
  try {
    data = await res.json();
  } catch {
    data = (await res.text()) as unknown as T;
  }
  return { status: res.status, data };
};
